import { useMemo } from 'react';

import {
  FolderType,
  PermissionEnumsDtoFolderPermissionKey,
  PermissionEnumsDtoOrganizationPermissionKey,
  PermissionEnumsDtoPlatformPermissionKey,
  PermissionEnumsDtoProcessPermissionKey,
  PermissionEnumsDtoSubfolderPermissionKey,
  PermissionEnumsDtoSuborganizationPermissionKey,
  PermissionEnumsDtoTalentPermissionKey,
  PermissionEnumsDtoUserPermissionKey,
  useGetFolderPermissions,
  useGetOrganizationPermissions,
  useGetPlatformPermissions,
  useGetProcessPermissions,
  useGetSubfolderPermissions,
  useGetSuborganizationPermissions,
  useGetTalentPermissions,
  useGetUserPermissions
} from '@/api/generated';
import { useLocation } from '@/hooks/entities/useLocation';
import { useProcess } from '@/hooks/entities/useProcess';
import { useTalent } from '@/hooks/entities/useTalent';
import { useUser } from '@/hooks/entities/useUser';
import { getErrorStatus } from '@/utils/error';

enum PermissionOperation {
  AND = 'AND',
  OR = 'OR'
}

export type PermissionKey =
  | PermissionEnumsDtoFolderPermissionKey
  | PermissionEnumsDtoSubfolderPermissionKey
  | PermissionEnumsDtoProcessPermissionKey
  | PermissionEnumsDtoTalentPermissionKey
  | PermissionEnumsDtoUserPermissionKey
  | PermissionEnumsDtoOrganizationPermissionKey
  | PermissionEnumsDtoSuborganizationPermissionKey
  | PermissionEnumsDtoPlatformPermissionKey;

export type PermissionContextVariables = {
  locationSlug?: string;
  processId?: string;
  talentId?: string;
  userId?: string;
  organizationSlug?: string;
  locationType?: FolderType;
};

enum PermissionScope {
  FOLDER = 'Folder',
  SUBFOLDER = 'Subfolder',
  PROCESS = 'Process',
  TALENT = 'Talent',
  USER = 'User',
  ORGANIZATION = 'Organization',
  SUBORGANIZATION = 'Suborganization',
  PLATFORM = 'Platform'
}
export const PermissionKeys = {
  ...PermissionEnumsDtoFolderPermissionKey,
  ...PermissionEnumsDtoSubfolderPermissionKey,
  ...PermissionEnumsDtoProcessPermissionKey,
  ...PermissionEnumsDtoTalentPermissionKey,
  ...PermissionEnumsDtoUserPermissionKey,
  ...PermissionEnumsDtoOrganizationPermissionKey,
  ...PermissionEnumsDtoSuborganizationPermissionKey,
  ...PermissionEnumsDtoPlatformPermissionKey
} as const;

const ALL_PERMISSION_SCOPES = new Set([
  PermissionScope.FOLDER,
  PermissionScope.SUBFOLDER,
  PermissionScope.PROCESS,
  PermissionScope.TALENT,
  PermissionScope.USER,
  PermissionScope.ORGANIZATION,
  PermissionScope.SUBORGANIZATION,
  PermissionScope.PLATFORM
]);

const PERMISSION_NAME_SEPARATOR = '.' as const;

type UsePermissionsResult = {
  value: boolean;
  isLoading: boolean;
  errors: unknown[];
  isAccessDenied: boolean;
};

const extractScope = (permission: PermissionKey): PermissionScope | null => {
  const [scope] = permission.split(PERMISSION_NAME_SEPARATOR);

  if (ALL_PERMISSION_SCOPES.has(scope as PermissionScope)) {
    return scope as PermissionScope;
  }

  return null;
};

const extractPermissionName = (permission: string): string | null => {
  if (permission.includes(PERMISSION_NAME_SEPARATOR)) {
    const [, name] = permission.split(PERMISSION_NAME_SEPARATOR);

    return name;
  }

  return null;
};

const permissionChecker =
  (allPermissions: Record<PermissionScope, Record<string, boolean> | undefined>) => (permission: PermissionKey) => {
    const scope = extractScope(permission);
    const permissionName = extractPermissionName(permission);
    if (scope && permissionName) {
      const permissionsForScope = allPermissions[scope];

      return permissionsForScope?.[permissionName] ?? false;
    }

    return false;
  };

const usePermissions = ({
  permissions,
  operation,
  variables
}: {
  permissions: PermissionKey[];
  operation: PermissionOperation;
  variables?: PermissionContextVariables;
}): UsePermissionsResult => {
  const location = useLocation();
  const process = useProcess();
  const talent = useTalent();
  const user = useUser();

  const organizationSlug = variables?.organizationSlug ?? location.organizationSlug;
  const locationSlug = variables?.locationSlug ?? location.locationSlug;
  const processId = variables?.processId ?? process.processId;
  const talentId = variables?.talentId ?? talent.talentId;
  const userId = variables?.userId ?? user.userId;
  const locationType = variables?.locationType ?? location.locationType;

  const scopesSet = useMemo(() => new Set(permissions.map(extractScope)), [permissions]);

  const isFolderEnabled =
    scopesSet.has(PermissionScope.FOLDER) && !!organizationSlug && !!locationSlug && locationType === FolderType.FOLDER;

  const isSubfolderEnabled =
    scopesSet.has(PermissionScope.SUBFOLDER) && !!organizationSlug && locationType === FolderType.SUB_FOLDER;

  const isProcessEnabled = scopesSet.has(PermissionScope.PROCESS) && !!processId;
  const isTalentEnabled = scopesSet.has(PermissionScope.TALENT) && !!talentId;
  const isUserEnabled = scopesSet.has(PermissionScope.USER) && !!userId;

  const isOrganizationEnabled =
    scopesSet.has(PermissionScope.ORGANIZATION) &&
    !!organizationSlug &&
    (!variables?.locationType || variables?.locationType === FolderType.ORGANIZATION);

  const isSuborganizationEnabled =
    scopesSet.has(PermissionScope.SUBORGANIZATION) && !!locationSlug && locationType === FolderType.SUB_ORGANIZATION;

  const isPlatformEnabled = scopesSet.has(PermissionScope.PLATFORM);

  const {
    data: folderPermissions,
    isLoading: isLoadingFolder,
    error: folderError
  } = useGetFolderPermissions(organizationSlug!, locationSlug!, {
    query: {
      enabled: isFolderEnabled
    }
  });

  const {
    data: subfolderPermissions,
    isLoading: isLoadingSubfolder,
    error: subfolderError
  } = useGetSubfolderPermissions(organizationSlug!, locationSlug!, {
    query: {
      enabled: isSubfolderEnabled
    }
  });

  const {
    data: processPermissions,
    isLoading: isLoadingProcess,
    error: processError
  } = useGetProcessPermissions(processId!, {
    query: { enabled: isProcessEnabled }
  });

  const {
    data: talentPermissions,
    isLoading: isLoadingTalent,
    error: talentError
  } = useGetTalentPermissions(talentId!, {
    query: { enabled: isTalentEnabled }
  });

  const {
    data: userPermissions,
    isLoading: isLoadingUser,
    error: userError
  } = useGetUserPermissions(userId!, {
    query: { enabled: isUserEnabled }
  });

  const {
    data: organizationPermissions,
    isLoading: isLoadingOrganization,
    error: organizationError
  } = useGetOrganizationPermissions(organizationSlug!, {
    query: { enabled: isOrganizationEnabled }
  });

  const {
    data: suborganizationPermissions,
    isLoading: isLoadingSuborganization,
    error: suborganizationError
  } = useGetSuborganizationPermissions(locationSlug!, {
    query: {
      enabled: isSuborganizationEnabled
    }
  });

  const {
    data: platformPermissions,
    isLoading: isLoadingPlatform,
    error: platformError
  } = useGetPlatformPermissions({
    query: { enabled: isPlatformEnabled }
  });

  const errors = useMemo(
    () =>
      [
        isFolderEnabled && folderError,
        isSubfolderEnabled && subfolderError,
        isProcessEnabled && processError,
        isTalentEnabled && talentError,
        isUserEnabled && userError,
        isOrganizationEnabled && organizationError,
        isSuborganizationEnabled && suborganizationError,
        isPlatformEnabled && platformError
      ].filter(Boolean),
    [
      isFolderEnabled,
      folderError,
      isSubfolderEnabled,
      subfolderError,
      isProcessEnabled,
      processError,
      isTalentEnabled,
      talentError,
      isUserEnabled,
      userError,
      isOrganizationEnabled,
      organizationError,
      isSuborganizationEnabled,
      suborganizationError,
      isPlatformEnabled,
      platformError
    ]
  );

  const isLoading =
    (isFolderEnabled && isLoadingFolder) ||
    (isSubfolderEnabled && isLoadingSubfolder) ||
    (isProcessEnabled && isLoadingProcess) ||
    (isTalentEnabled && isLoadingTalent) ||
    (isUserEnabled && isLoadingUser) ||
    (isOrganizationEnabled && isLoadingOrganization) ||
    (isSuborganizationEnabled && isLoadingSuborganization) ||
    (isPlatformEnabled && isLoadingPlatform);

  const allPermissions = useMemo(
    () => ({
      [PermissionScope.FOLDER]: isFolderEnabled ? folderPermissions : null,
      [PermissionScope.SUBFOLDER]: isSubfolderEnabled ? subfolderPermissions : null,
      [PermissionScope.PROCESS]: isProcessEnabled ? processPermissions : null,
      [PermissionScope.TALENT]: isTalentEnabled ? talentPermissions : null,
      [PermissionScope.USER]: isUserEnabled ? userPermissions : null,
      [PermissionScope.ORGANIZATION]: isOrganizationEnabled ? organizationPermissions : null,
      [PermissionScope.SUBORGANIZATION]: isSuborganizationEnabled ? suborganizationPermissions : null,
      [PermissionScope.PLATFORM]: isPlatformEnabled ? platformPermissions : null
    }),
    [
      isFolderEnabled,
      folderPermissions,
      isSubfolderEnabled,
      subfolderPermissions,
      isProcessEnabled,
      processPermissions,
      isTalentEnabled,
      talentPermissions,
      isUserEnabled,
      userPermissions,
      isOrganizationEnabled,
      organizationPermissions,
      isSuborganizationEnabled,
      suborganizationPermissions,
      isPlatformEnabled,
      platformPermissions
    ]
  );

  const value = useMemo(() => {
    const checkPermission = permissionChecker(
      allPermissions as Record<PermissionScope, Record<string, boolean> | undefined>
    );

    switch (operation) {
      case PermissionOperation.AND:
        return permissions.every(checkPermission);
      case PermissionOperation.OR:
        return permissions.some(checkPermission);
      default:
        throw new Error(`Invalid operation: ${operation}`);
    }
  }, [permissions, allPermissions, operation]);

  const isAccessDenied = useMemo(
    () => errors.some(error => [401, 403, 404].includes(getErrorStatus(error) ?? 0)),
    [errors]
  );

  return { value, isLoading, errors, isAccessDenied };
};

export const useAllPermissions = ({
  permissions,
  variables
}: {
  permissions: PermissionKey[];
  variables?: PermissionContextVariables;
}) => usePermissions({ permissions, operation: PermissionOperation.AND, variables });

export const useAnyPermissions = ({
  permissions,
  variables
}: {
  permissions: PermissionKey[];
  variables?: PermissionContextVariables;
}) => usePermissions({ permissions, operation: PermissionOperation.OR, variables });
